import { createEntityStore } from "@/core/factory/store"
import { Document, DOCUMENT_ENTITY, DocumentGenerationDiff, createEmptyDocument } from "./document.types"
import { noContext } from "@/core/utils/noContext"
import { createSelector } from "@reduxjs/toolkit"
import { isEqual } from "lodash"
import { getDocumentOutdatedNotificationTypes, isDocumentOutdated } from "../utils/document-outdated.utils"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"

function sortStringList(list: string[] = []) {
  return [...list].sort()
}

function normalizeAttributes(attributes: Record<string, any> = {}) {
  return Object.keys(attributes)
    .sort()
    .reduce<Record<string, any>>((normalized, key) => {
      const value = attributes[key]
      if (value === null || value === undefined || value === "") return normalized

      normalized[key] = value
      return normalized
    }, {})
}

function normalizeNumber(value: any) {
  if (value === null || value === undefined || value === "") return null
  const numberValue = Number(value)

  return Number.isFinite(numberValue) ? numberValue : value
}

function normalizePhysicalRisk(risk: any) {
  return {
    id: risk.id,
    attributes: normalizeAttributes(risk.attributes),
    frequency: normalizeNumber(risk.frequency),
    severity: normalizeNumber(risk.severity),
    prevention: normalizeNumber(risk.prevention),
    photoId: risk.photoId ?? null,
    RiskFamilyId: risk.RiskFamilyId ?? null,
    siteIds: sortStringList(risk.siteIds),
    populationIds: sortStringList(risk.populationIds)
  }
}

function getPhysicalRiskLabel(risk: any) {
  const attributeValues = Object.values(risk.attributes ?? {}).filter((value) => value != null && value !== "")
  return risk.cName || attributeValues[0] || "Risque physique"
}

function getLatestDuerpSnapshot(documents: Document[]) {
  return [...documents]
    .filter((document) => document.type === "duerp" && document.content?.physicalRisks)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
}

function getDiffForCollection(
  snapshotItems: any[] = [],
  currentItems: any[] = [],
  normalize: (item: any) => any,
  scope: DocumentGenerationDiff["scope"],
  getLabel: (item: any) => string
): DocumentGenerationDiff[] {
  const snapshotById = new Map(snapshotItems.map((item) => [item.id, item]))
  const currentById = new Map(currentItems.map((item) => [item.id, item]))
  const diff: DocumentGenerationDiff[] = []

  currentItems.forEach((item) => {
    const snapshotItem = snapshotById.get(item.id)

    if (!snapshotItem) {
      diff.push({ id: item.id, type: "added", scope, label: getLabel(item) })
      return
    }

    if (!isEqual(normalize(snapshotItem), normalize(item))) {
      diff.push({ id: item.id, type: "updated", scope, label: getLabel(item) })
    }
  })

  snapshotItems.forEach((item) => {
    if (!currentById.has(item.id)) {
      diff.push({ id: item.id, type: "removed", scope, label: getLabel(item) })
    }
  })

  return diff
}

const createDocumentStore = () => {
  const store = createEntityStore<Document, null>(DOCUMENT_ENTITY, createEmptyDocument, noContext)

  const generationDiff = createSelector(
    [store.selectors.list, physicalRiskStore.selectors.list],
    (documents, physicalRisks): DocumentGenerationDiff[] => {
      const latestSnapshot = getLatestDuerpSnapshot(documents)

      if (!latestSnapshot) {
        return []
      }

      const snapshotContent = latestSnapshot.content ?? {}

      return [
        ...getDiffForCollection(
          snapshotContent.physicalRisks,
          physicalRisks,
          normalizePhysicalRisk,
          "physicalRisk",
          getPhysicalRiskLabel
        )
      ]
    }
  )

  const needsGeneration = createSelector([generationDiff], (diff) => diff.length > 0)
  const outdated = createSelector([store.selectors.list], (documents) => isDocumentOutdated(documents))
  const outdatedNotificationTypes = createSelector([store.selectors.list], (documents) =>
    getDocumentOutdatedNotificationTypes(documents)
  )

  return {
    ...store,
    selectors: {
      ...store.selectors,
      generationDiff,
      needsGeneration,
      outdated,
      outdatedNotificationTypes
    },
    thunks: {
      ...store.thunks
    }
  }
}

const documentStore = createDocumentStore()
export default documentStore
